import {
  BackgroundJobStatusValue,
  isActiveBackgroundJobStatus,
} from 'src/fetch/backgroundJobs/backgroundJobStatus'

import type { TrackedBackgroundJob } from './types'

export const BACKGROUND_JOB_GENERIC_ERROR_MESSAGE = 'Something went wrong.'

export type BackgroundJobBarVariant = 'inProgress' | 'success' | 'warning' | 'error' | 'cancelled'

export type BackgroundJobBarContent = {
  variant: BackgroundJobBarVariant
  message: string
  description?: string
}

const variantByBarVariant: Record<BackgroundJobBarVariant, string> = {
  inProgress: 'bg-amber-50 border-amber-200 text-amber-950',
  success: 'bg-green-50 border-green-200 text-green-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-950',
  error: 'bg-red-50 border-red-200 text-red-900',
  cancelled: 'bg-neutral-50 border-neutral-300 text-neutral-600',
}

export const backgroundJobBarVariantClassName = (variant: BackgroundJobBarVariant) =>
  variantByBarVariant[variant]

/** In-progress copy — e.g. "Reassigning record owner — 40/1,234". */
export const getInProgressBarContent = (job: TrackedBackgroundJob): BackgroundJobBarContent => {
  if (job.totalCount === 0) {
    return {
      variant: 'inProgress',
      message: `${job.pillActionLabel} — Starting…`,
    }
  }
  return {
    variant: 'inProgress',
    message: `${job.pillActionLabel} — ${job.processedCount.toLocaleString()}/${job.totalCount.toLocaleString()}`,
  }
}

export const getCompletionBarContent = (job: TrackedBackgroundJob): BackgroundJobBarContent => {
  const action = job.pillTerminalActionLabel
  const succeeded = job.successCount.toLocaleString()
  const failed = job.errorCount.toLocaleString()

  if (job.status === BackgroundJobStatusValue.Failed) {
    if (job.errorCount > 0) {
      return {
        variant: 'error',
        message: `${action} failed`,
        description: `${failed} records could not be updated.`,
      }
    }
    return {
      variant: 'error',
      message: `${action} failed`,
      description: BACKGROUND_JOB_GENERIC_ERROR_MESSAGE,
    }
  }

  if (job.status === BackgroundJobStatusValue.PartiallyCompleted) {
    const recordFailureDescription = `${failed} records failed.`
    return {
      variant: 'warning',
      message: `${action} finished with errors — ${succeeded}/${job.totalCount.toLocaleString()}`,
      description: recordFailureDescription,
    }
  }

  return {
    variant: 'success',
    message: `${action} complete — ${succeeded} records`,
  }
}

export const getCancelledBarContent = (job: TrackedBackgroundJob): BackgroundJobBarContent => {
  const action = job.pillTerminalActionLabel
  const updated = job.successCount.toLocaleString()

  if (job.successCount > 0) {
    return {
      variant: 'cancelled',
      message: `${action} cancelled`,
      description: `${updated} records were updated before cancel.`,
    }
  }

  return {
    variant: 'cancelled',
    message: `${action} cancelled`,
    description: 'No records were updated.',
  }
}

/** Completed/Cancelled pills auto-ack after onComplete finishes (or when no callback was registered). */
export const shouldAutoDismissTerminalBackgroundJob = (
  job: Pick<TrackedBackgroundJob, 'isInTerminalStatus' | 'status'>
) =>
  job.isInTerminalStatus &&
  (job.status === BackgroundJobStatusValue.Completed ||
    job.status === BackgroundJobStatusValue.Cancelled)

export const getJobBarContent = (job: TrackedBackgroundJob): BackgroundJobBarContent => {
  if (isActiveBackgroundJobStatus(job.status)) {
    return getInProgressBarContent(job)
  }
  if (job.status === BackgroundJobStatusValue.Cancelled) {
    return getCancelledBarContent(job)
  }
  return getCompletionBarContent(job)
}
